import { useMemo } from 'react';
import IconV2 from './v2/IconV2';
import { FiArrowRight, FiLock, FiUser, FaInbox } from '@/components/react-icons/main';
import { AddIcon } from '@/lib/constants/icons.js';
import computeColor from '@/lib/computeColor';
import { useSelector } from 'react-redux';
import selectors from '@/redux/selectors/selectors';
import Tag from './Tag';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import SelectPopoverV2 from './v2/SelectPopoverV2.jsx';
import { isCompanyStaffAccessRestricted } from 'shared/schema/staffAccess.js';

const CompanyIcon = ({ name }) => {
  const backgroundColor = useMemo(() => computeColor(name || ''), [name]);
  return (
    <div className="item-icon" style={{ backgroundColor }}>
      {name?.[0]?.toUpperCase()}
    </div>
  );
};

// Main component
const CompanyPopover = ({
  children, //
  handleCreateNewOrganization,
  onSearch,
  search,
  onChangeCompany,
  options = [],
  selectedCompany,
  recentCompanies = [],
  toggleShowPersonal,
  showPersonal,
  debouncedSearch,
  isAdmin
}) => {
  const userId = useSelector(selectors.userId);
  const isLoading = useArgSelector(selectors.isLoading, 'getUserCompaniesOptions') && search;

  // Group and organize company data
  const groupedData = useMemo(() => {
    const organizeCompanyData = () => {
      const sortCompanies = (companies) => {
        return [...companies].sort((a, b) => {
          // Sort by workflow count first
          const workflowDiff = (b.activeWorkflowCount || 0) - (a.activeWorkflowCount || 0);
          if (workflowDiff !== 0) return workflowDiff;
          // Then alphabetically
          return (a.name || '').localeCompare(b.name || '');
        });
      };

      let filteredOptions = [...options];
      if (isAdmin) {
        const personalAccounts = new Map();
        options.forEach((company) => {
          if (company.isPersonal) {
            const existing = personalAccounts.get(company.userId);
            if (!existing || company.activeWorkflowCount > existing.activeWorkflowCount) {
              personalAccounts.set(company.userId, company);
            }
          }
        });
        filteredOptions = options.filter((company) => !company.isPersonal || personalAccounts.get(company.userId)?.id === company.id);
      }

      if (!isAdmin && filteredOptions.length <= 5) {
        const sortedOptions = sortCompanies(filteredOptions);
        return !search && selectedCompany ? [{ ...selectedCompany, selected: true }, ...sortedOptions.filter((opt) => opt.id !== selectedCompany?.id)] : sortedOptions;
      }

      // Filter recent companies that aren't in options and aren't the selected company
      const filteredRecentCompanies = !search ? recentCompanies.filter((company) => !filteredOptions.some((opt) => opt.id === company.id) && company.id !== selectedCompany?.id) : [];

      // Only include separator if there are filtered recent companies
      const recentEntries = filteredRecentCompanies.length > 0 ? [{ type: 'separator' }, ...filteredRecentCompanies] : [];

      const sortedOptions = sortCompanies(filteredOptions);
      return !search && selectedCompany ? [{ ...selectedCompany, selected: true }, ...sortedOptions, ...recentEntries] : [...sortedOptions, ...recentEntries];
    };

    return isLoading ? [] : organizeCompanyData();
  }, [search, recentCompanies, selectedCompany, options, isAdmin, debouncedSearch, isLoading]);

  const handleItemSelect = (item) => {
    onChangeCompany(item);
  };

  const footerActions = useMemo(() => {
    const actions = [];

    if (isAdmin) {
      actions.push({
        id: 'show-personal',
        icon: FiUser,
        text: 'Show personal accounts',
        onClick: toggleShowPersonal,
        showToggle: true,
        toggleProps: {
          onClick: toggleShowPersonal,
          size: 'xs',
          active: showPersonal
        },
        closeOnClick: false
      });
    }

    if (!search) {
      actions.push({
        id: 'create-org',
        icon: AddIcon,
        text: 'Create organization',
        onClick: handleCreateNewOrganization
      });
    }

    return actions;
  }, [isAdmin, showPersonal, search, toggleShowPersonal, handleCreateNewOrganization]);

  const handleSearchValue = (value) => {
    if (!onSearch) {
      return;
    }
    onSearch({ target: { value } });
  };

  const renderCompanyOption = ({ option, isActive, isSelected, optionRef, onSelect: handleSelect }) => {
    const label = isAdmin && option?.isPersonal && option?.userId !== userId ? option.username : option.name;
    const tagLabel = option?.jobCount > option?.activeWorkflowCount ? `${option?.jobCount} jobs` : `${option?.activeWorkflowCount} workflows`;

    return (
      <div ref={optionRef} data-active={isActive} data-selected={isSelected} className="select-popover-item company-popover-item" onClick={() => handleSelect(option)} tabIndex={0}>
        <CompanyIcon name={label} />
        <span className="item-name">
          {label}
          {isAdmin && option?.isPersonal && option?.userId !== userId && <span> (Personal)</span>}
        </span>
        {isAdmin && isCompanyStaffAccessRestricted(option) && <IconV2 className="restricted-lock" icon={FiLock} size="sm" variant="light-medium" title="Restricted" />}
        <Tag className="item-tag">{tagLabel}</Tag>
        <IconV2 className="arrow" icon={FiArrowRight} variant="light" />
      </div>
    );
  };

  return (
    <SelectPopoverV2 //
      className="comp-company-popover"
      options={groupedData}
      onSelect={handleItemSelect}
      renderOption={renderCompanyOption}
      footerActions={footerActions}
      searchValue={search}
      onSearch={handleSearchValue}
      useSearch={true}
      loading={isLoading}
      emptyLabel="No companies found"
      emptyIcon={FaInbox}
      clearSearchOnClose={true}>
      {children}
    </SelectPopoverV2>
  );
};

export default CompanyPopover;
